/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { devices, fontFamilies } from "components/shared/Styles";
import * as React from "react";
import { StyledEnhanceSlider } from "components/shared/StyledEnhanceSlider";
import { intlPercentFormatter } from "utilities/UserSettingsUtilities";
import { InfoIcon } from "components/EnhancedSpeech/ProTierEnhanceSpeech/InfoContainer";

interface StrengthAdjusterProps {
  enhanceStrengthPercent: number;
  onChange: (value: number) => void;
  backgroundPercent: number;
  onChangeBackground: (value: number) => void;
  enhanceEnabled: boolean;
  showBackgroundSlider?: boolean;
  onChangeComplete?: (value: number) => void;
  toggleSlot?: React.ReactNode;
  handleSize?: "s" | "l";
}

export const StrengthAdjuster: React.FC<StrengthAdjusterProps> = ({
  enhanceStrengthPercent,
  onChange,
  backgroundPercent,
  onChangeBackground,
  enhanceEnabled,
  showBackgroundSlider,
  onChangeComplete,
  toggleSlot,
  handleSize = "l",
}) => {
  return (
    <>
      {toggleSlot}
      <StrengthControlContainer
        disabled={!enhanceEnabled}
        data-testid="speech-strength-container"
      >
        <StrengthControlLabelContainer>
          <StrengthControlLabel>
            <EnhanceSliderTitle>
              {I18n.t("enhance_speech.controls.sliders.enhance_strength.title")}
            </EnhanceSliderTitle>
            <InfoIcon placement="top">
              {I18n.t("enhance_speech.controls.sliders.enhance_strength.info")}
            </InfoIcon>
          </StrengthControlLabel>

          <Strength data-testid="enhance-strength-label">
            {intlPercentFormatter().format(enhanceStrengthPercent / 100)}
          </Strength>
        </StrengthControlLabelContainer>
        <StyledEnhanceSlider
          disabled={!enhanceEnabled}
          onChange={onChange}
          onChangeComplete={onChangeComplete}
          value={enhanceStrengthPercent}
          ariaLabelledByForHandle="enhance-strength-label"
          ariaValueTextFormatterForHandle={(value): string => `${value}%`}
          handleSize={handleSize}
        />
        <StrengthControlLabelContainer>
          <div>{I18n.t("enhance_speech.controls.sliders.less")}</div>
          <div>{I18n.t("enhance_speech.controls.sliders.more")}</div>
        </StrengthControlLabelContainer>
      </StrengthControlContainer>
      {showBackgroundSlider && (
        <StrengthControlContainer
          disabled={!enhanceEnabled}
          data-testid="background-strength-container"
        >
          <StrengthControlLabelContainer>
            <StrengthControlLabel>
              <BackgroundSliderTitle>
                {I18n.t(
                  "enhance_speech.controls.sliders.background_noise.title"
                )}
              </BackgroundSliderTitle>
              <InfoIcon placement="top">
                {I18n.t(
                  "enhance_speech.controls.sliders.background_noise.info"
                )}
              </InfoIcon>
            </StrengthControlLabel>
            <Strength data-testid="background-strength-label">
              {intlPercentFormatter().format(backgroundPercent / 100)}
            </Strength>
          </StrengthControlLabelContainer>
          <StyledEnhanceSlider
            disabled={!enhanceEnabled}
            onChange={onChangeBackground}
            onChangeComplete={onChangeComplete}
            value={backgroundPercent}
            railBackground="linear-gradient(90deg, #FFD1BF 0%, #FFA685 21.87%, #FF7243 99.43%)"
            trackBackgroundColor="#FFE2D7"
            ariaLabelledByForHandle="background-strength-label"
            ariaValueTextFormatterForHandle={(value): string => `${value}%`}
            handleSize={handleSize}
          />
          <StrengthControlLabelContainer>
            <div>{I18n.t("enhance_speech.controls.sliders.less")}</div>
            <div>{I18n.t("enhance_speech.controls.sliders.more")}</div>
          </StrengthControlLabelContainer>
        </StrengthControlContainer>
      )}
    </>
  );
};

const StrengthControlLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  color: #222;
  font: normal normal normal 16px/20px ${fontFamilies};
`;

const StrengthControlLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: left;
  gap: 8px;
`;

interface StrengthControlContainerProps {
  disabled?: boolean;
}

const StrengthControlContainer = styled.div<StrengthControlContainerProps>`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 16px;
  background: #fff;

  /* Lavender Haze */
  box-shadow: 0px 12px 24px 0px rgba(57, 63, 199, 0.09),
    0px 0px 2px 0px rgba(0, 2, 56, 0.15), 0px 3px 4px 0px rgba(0, 2, 56, 0.03);
  gap: 8px;
  align-self: stretch;
  opacity: ${({ disabled }): number => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }): string => (disabled ? "none" : "auto")};
  filter: ${({ disabled }): string => (disabled ? "grayscale(50%)" : "none")};
`;

const EnhanceSliderTitle = styled.h2`
  font: normal normal 800 18px/23.4px ${fontFamilies};
  background: var(
    --Plum,
    linear-gradient(133deg, #5266ff 5.68%, #b871ff 96.33%)
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const BackgroundSliderTitle = styled.h2`
  font: normal normal 800 18px/23.4px ${fontFamilies};
  color: #f16410;
`;

const Strength = styled.span`
  font: normal normal normal 18px/22px ${fontFamilies};
`;
